import { useCallback, useMemo } from "react";
import { useIndexedDB } from "react-indexed-db-hook";
import { VolunteerApi } from "services/api/volunteer";
import { VolunteerData } from "services/api/volunteer/types";
import { initIndexedDbIfMissingStore } from "utils/indexedDbRecovery";

export const useVolunteerController = () => {
	const volunteerDB = initIndexedDbIfMissingStore("volunteer");
	const volunteerApi = useMemo(() => new VolunteerApi(), []);

	/**
	 * 从 API 获取志愿者列表并保存到 IndexedDB
	 */
	const fetchAndSaveVolunteers = useCallback(async (eventId: string) => {
		if (!eventId) {
			console.warn("fetchAndSaveVolunteers: eventId is required");
			return [];
		}
		
		try {
			const response = await volunteerApi.getVolunteer(eventId);
			
			// 处理不同的响应格式
			let volunteers = [];
			if (Array.isArray(response)) {
				// 如果响应直接是数组
				volunteers = response;
			} else if (response.data && Array.isArray(response.data)) {
				// 如果响应有 data 字段且是数组
				volunteers = response.data;
			} else if (response.data) {
				// 如果 data 存在但不是数组，转换为数组
				volunteers = [response.data];
			}

			// 保存到 IndexedDB
			const savePromises = volunteers.map(volunteer => 
				volunteerDB.update({
					...volunteer,
					eventId, // 添加 eventId 用于查询
					cachedAt: Date.now(), // 缓存时间
				})
			);

			await Promise.all(savePromises);
			console.log(`Successfully cached ${volunteers.length} volunteers for event ${eventId}`);
			
			return volunteers;
		} catch (error) {
			console.error("Failed to fetch and save volunteers:", error);
			throw error;
		}
	}, [volunteerDB, volunteerApi]);

	/**
	 * 从 IndexedDB 获取志愿者列表
	 */
	const getVolunteersFromCache = useCallback(async (eventId: string): Promise<VolunteerData[]> => {
		try {
			const allVolunteers = await volunteerDB.getAll();
			
			// 过滤出当前活动的志愿者
			const volunteers = allVolunteers.filter((vol: any) => vol.eventId === eventId);
			
			return volunteers;
		} catch (error) {
			console.error("Failed to get volunteers from cache:", error);
			return [];
		}
	}, [volunteerDB]);

	/**
	 * 获取志愿者列表（优先从缓存，如果在线则更新）
	 */
	const getVolunteers = useCallback(async (eventId: string, forceRefresh: boolean = false): Promise<VolunteerData[]> => {
		// INSERT_YOUR_CODE

		if (!eventId) {
			console.warn("getVolunteers: eventId is required");
			return [];
		}

		// 先从缓存获取
		const cachedVolunteers = await getVolunteersFromCache(eventId);

		// 如果需要强制刷新或者没有缓存，尝试从 API 获取
		if (forceRefresh || cachedVolunteers.length === 0) {
			try {
				return await fetchAndSaveVolunteers(eventId);
			} catch (error) {
				// 如果 API 失败，返回缓存数据
				console.warn("Failed to fetch from API, using cached data");
				return cachedVolunteers;
			}
		}

		return cachedVolunteers;
	}, [getVolunteersFromCache, fetchAndSaveVolunteers]);

	/**
	 * 清除志愿者缓存
	 */
	const clearVolunteerCache = useCallback(async (eventId?: string) => {
		try {
			if (eventId) {
				// 清除特定活动的志愿者
				const allVolunteers = await volunteerDB.getAll();
				const deletePromises = allVolunteers
					.filter((vol: any) => vol.eventId === eventId)
					.map((vol: any) => volunteerDB.deleteRecord(vol.volunteerId));
				await Promise.all(deletePromises);
			} else {
				// 清除所有志愿者
				await volunteerDB.clear();
			}
		} catch (error) {
			console.error("Failed to clear volunteer cache:", error);
		}
	}, [volunteerDB]);

	return useMemo(
		() => ({
			fetchAndSaveVolunteers,
			getVolunteersFromCache,
			getVolunteers,
			clearVolunteerCache,
		}),
		[fetchAndSaveVolunteers, getVolunteersFromCache, getVolunteers, clearVolunteerCache],
	);
};
